'use client';

import { useEffect } from 'react';
import { captureBrowserException } from '@/lib/posthog-browser';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to console
    console.error('Global error boundary caught:', error);

    // This boundary replaces the root layout, so <PostHogProvider> never mounts and the SDK
    // may not be initialised — captureBrowserException brings it up if needed.
    captureBrowserException(error, {
      error_digest: error.digest,
      boundary_location: 'global',
    });
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div className="flex min-h-screen items-center justify-center p-4 bg-background text-foreground">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-red-500/10 p-3">
                <AlertCircle className="size-8 text-red-500" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Critical Error
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The application encountered a critical error. Please try refreshing the page.
              </p>
            </div>

            {error.digest && (
              <div className="rounded-lg bg-gray-100 dark:bg-gray-800 p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Error ID: {error.digest}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                onClick={reset}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
              >
                Try again
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
              >
                Go to homepage
              </Button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
